import React, { Dispatch, useCallback, useMemo, useRef, useState } from 'react';
import {
    Box,
    SxProps,
    IconButton,
    Stack,
    Chip,
    Button,
    Divider,
    CircularProgress,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiPlusCircleOutline, mdiContentCopy } from '@mdi/js';
import { headingFontBold, vars } from '../../../Theme';
import { IAppReduxStore } from '../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import useBreakpoints from '../../../hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import DocDetailsMode from '../../General/DocControls/DocDetailsMode';
import DocDetailsControls from '../../General/DocControls/DocDetailsControls';
import FormFieldView from '../../General/TextBlocks/FormFieldView';
import AppTooltip from '../../General/AppTooltip/AppTooltip';
import DocTypeIcon from '../../General/Icons/DocTypeIcon';
import MainField from '../../General/TextBlocks/MainField';
import { parseDate, parseUsername } from '../../../util/DataUtils';
import DocStatusIcon from '../../General/Icons/DocStatusIcon';
import {
    closeDocumentDetailsDialog,
    hasSomePermission,
    toggleExpandDocumentDetailsDialog,
    handleEditMode,
    closeLeaveConfirmDialog,
} from '../../../redux/project/documents/sliceProjectDocumentDetails';
import AppIcon from '../../General/Icons/AppIcon';
import FormDesigner from '../../General/FormDesigner/FormDesigner';
import UserAvatar from '../../General/Header/UserAvatar';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../General/Toast/Toast';
import { handleCopyText } from '../../../util/FileUtils';
import { getDocTypeOrDefault } from '../../../util/DocUtils';
import LeaveConfirmDialog from '../../General/Dialogs/LeaveConfirmDialog/LeaveConfirmDialog';
import { filingViewKeysMapperWithDefaultSubject } from '../../General/DocTable/DocTableActions';
import { useParams } from 'react-router-dom';
import { fullSize } from '../../../Constants';
import { fetchImportViewData } from '../../../util/ImportUtils';

/** Styles for docIndex Chip. */
const docIndexChipSx: SxProps = {
    color: vars.white,
    ...headingFontBold,
    fontSize: '12px',
    height: '24px',
    ml: 1,
};

/**
 * Defines properties for the grid box.
 * @param isMobile Defines screen size.
 * @returns Properties for the grid box, depending on screen size.
 */
const gridBoxProps = (isMobile: boolean) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, ${isMobile ? '100%' : '50%'})`,
});

/**
 * Sets wrapper styling in accordance with view mode (Dialog or Browser tab).
 * @param isDialog Defines if the content Component is Dialog or Browser tab.
 * @returns Properties for the wrapper.
 */
const setWrapperStyles = (isDialog: boolean) =>
    isDialog ? {} : { position: 'relative', height: '100%', p: 2 };

/**
 * Sets the background color of the docIndex chip.
 * @param docStatus Status of the document.
 * @returns Background color of the docIndex chip.
 */
const setDocIndexChipColor = (docStatus: string) => ({
    backgroundColor: docStatus === 'OldIndex' ? vars.concrete : vars.brandGreen,
});

/**
 * Sets padding-left depending on screen size.
 * @param isMobile Defines screen size.
 * @returns Padding-left depending on screen size.
 */
const setLeftPadding = (isMobile: boolean) => ({
    pl: isMobile ? 0 : 1,
});

/**
 * Component that displays the document's details.
 */
export default ({ replicaId, isDialog }: IDocumentDetailsViewProps) => {
    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();
    const { category } = useParams();

    /** Configure component translation. */
    const { t, i18n } = useTranslation('translations', { keyPrefix: 'docDetailsView' });

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    /** State slice that controls the visibility of the cell's hover buttons. */
    const [showHoverBtns, setShowHoverBtns] = useState<boolean>(false);

    /** Access relevant state variables from the redux store. */
    const {
        data: documentDetailsData,
        isDialogExpanded,
        isLeaveConfirmDialogOpen,
        isSaveButtonDisabled,
        isSaveButtonLoaded,
        documentDetailsMode: documentDetailsModeData,
        docTypeKeyForTemplateCreation,
    } = useSelector((store: IAppReduxStore) => store.projectDocumentDetails);
    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);
    const structure = useSelector((store: IAppReduxStore) => store.projectViewData.structure.data);
    const { loadingRunDynamicAction } = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsDynamicSelection,
    );

    const hasSomePermissionData = useSelector(hasSomePermission);

    /** Ref for Formdesigner's form. */
    const submitRef = useRef<HTMLFormElement>(null);

    /** Detect screen size. */
    const { isMobile } = useBreakpoints();

    /** Initiates form's submit in the Formdesigner using ref. */
    const handleClickSaveBtn = () => {
        if (!submitRef?.current) return;
        submitRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));

        fetchImportViewData(replicaId);
    };

    /**
     * Closes the document's details Dialog.
     */
    const handleCloseDialog = () => {
        dispatch(closeDocumentDetailsDialog(true));
    };

    const handleCancelLeaveConfirmDialog = () => {
        dispatch(closeLeaveConfirmDialog());
    };

    const handleCloseLeaveConfirmDialog = () => {
        dispatch(closeLeaveConfirmDialog());
        dispatch(closeDocumentDetailsDialog(false));
    };

    const handleSaveChangesLeaveConfirmDialog = () => {
        handleClickSaveBtn();
        handleCloseLeaveConfirmDialog();
    };

    /**
     * Expandes the document's details Dialog and return back.
     */
    const handleExpandDialog = () => {
        dispatch(toggleExpandDocumentDetailsDialog());
    };

    /** Changes mode to edit. */
    const handleChangeMode = () => {
        dispatch(handleEditMode());
    };

    /**
     * Navigate to clicked category.
     */
    const handleTitleButtonClick = () => {
        window.open(`/document/${replicaId}/${documentDetailsData.UniversalID}`, '_blank');
        dispatch(closeDocumentDetailsDialog(true));
        isDialogExpanded && dispatch(toggleExpandDocumentDetailsDialog());
    };

    /**
     * Copy url using clipboard.
     */
    const handleCopyUrl = async () => {
        const url = `${window.location.origin}/document/${replicaId}/${documentDetailsData.UniversalID}`;
        handleCopyText(
            url,
            () =>
                enqueueSnackbar(t('copyUrlSuccess'), {
                    severity: ToastSeverity.Success,
                }),
            () =>
                enqueueSnackbar(t('copyUrlError'), {
                    severity: ToastSeverity.Error,
                }),
            () =>
                enqueueSnackbar(t('navigatorError'), {
                    severity: ToastSeverity.Error,
                }),
        );
    };

    /**
     * Defines title component as Link for the Dialog component.
     */
    const getTitleComponent = useCallback(
        ({ children }: { children: JSX.Element | JSX.Element[] }) => (
            <Box
                onMouseEnter={() => setShowHoverBtns(true)}
                onMouseLeave={() => setShowHoverBtns(false)}
                sx={{ display: 'flex', minHeight: '40px' }}
            >
                {isDialog && !docTypeKeyForTemplateCreation ? (
                    <Button
                        sx={{ p: 0, textTransform: 'capitalize', ...headingFontBold }}
                        onClick={handleTitleButtonClick}
                    >
                        {children}
                    </Button>
                ) : (
                    <>{children}</>
                )}
                {showHoverBtns && (
                    <>
                        {!docTypeKeyForTemplateCreation && (
                            <AppTooltip key='copyLink' title={t('copyLink')} placement='top-start'>
                                <IconButton
                                    onClick={handleCopyUrl}
                                    sx={{ borderRadius: 1, ml: 2, color: vars.font }}
                                >
                                    <Icon path={mdiContentCopy} size={1} />
                                </IconButton>
                            </AppTooltip>
                        )}
                    </>
                )}
            </Box>
        ),
        [isDialog, isDialogExpanded, docTypeKeyForTemplateCreation, showHoverBtns],
    );

    const defaultFormData = useMemo(() => {
        if (
            !docTypeKeyForTemplateCreation ||
            !Object.values(filingViewKeysMapperWithDefaultSubject).includes(
                docTypeKeyForTemplateCreation,
            )
        )
            return {};
        const structuresCategory = structure?.find(x => x.id === category)?.category;
        return structuresCategory ? { Subject: structuresCategory } : {};
    }, [docTypeKeyForTemplateCreation]);

    /** Defines the currentDocType. */
    const currentDocType = docTypes.find(
        type =>
            type.name ===
            (docTypeKeyForTemplateCreation ?? getDocTypeOrDefault(documentDetailsData, docTypes)),
    );

    /** Defines the description name. */
    const docName = useMemo(
        () =>
            currentDocType
                ? currentDocType.translations?.[i18n.language] ?? currentDocType.name
                : t('unknownDocumentType'),
        [currentDocType],
    );

    return (
        <Stack sx={{ ...setWrapperStyles(isDialog) }}>
            <Stack
                direction='row'
                sx={{ position: 'absolute', top: '0', right: '0', alignItems: 'center' }}
            >
                <DocDetailsMode
                    type={documentDetailsModeData}
                    docTypeKey={docTypeKeyForTemplateCreation}
                />
                <DocDetailsControls
                    isExpanded={isDialogExpanded}
                    handleCloseDialog={handleCloseDialog}
                    handleExpandDialog={handleExpandDialog}
                    handleClickSaveBtn={handleClickSaveBtn}
                    handleChangeMode={handleChangeMode}
                    docType={
                        docTypeKeyForTemplateCreation ??
                        getDocTypeOrDefault(documentDetailsData, docTypes)
                    }
                    docTypeKey={docTypeKeyForTemplateCreation}
                    universalId={documentDetailsData.UniversalID}
                    isDialog={isDialog}
                    isSaveButtonDisabled={isSaveButtonDisabled}
                    isSaveButtonLoaded={isSaveButtonLoaded}
                    isEditButtonDisabled={!hasSomePermissionData}
                />
            </Stack>
            <Box
                sx={{
                    ...gridBoxProps(isMobile),
                }}
            >
                <Box>
                    {getTitleComponent({
                        children: (
                            <FormFieldView
                                text={docName}
                                direction='row'
                                alignItems='center'
                                sx={{
                                    mb: 0,
                                    color: !currentDocType ? vars.errorRed : 'inherit',
                                }}
                            >
                                <DocTypeIcon
                                    type={
                                        docTypeKeyForTemplateCreation ??
                                        getDocTypeOrDefault(documentDetailsData, docTypes)
                                    }
                                    color='inherit'
                                    tooltipPlacement='top-start'
                                />
                            </FormFieldView>
                        ),
                    })}

                    <Stack
                        sx={{
                            mb: 1,
                        }}
                    >
                        {documentDetailsData.docIndex?.trim() ? (
                            <Stack
                                direction='row'
                                sx={{
                                    alignItems: 'center',
                                    minHeight: '40px',

                                    '&:hover': {
                                        backgroundColor: vars.bgGray,
                                    },
                                }}
                            >
                                <MainField
                                    text={documentDetailsData.docNumber}
                                    sx={{ fontSize: '16px', width: 'auto' }}
                                />
                                <Chip
                                    label={documentDetailsData.docIndex}
                                    sx={{
                                        ...setDocIndexChipColor(documentDetailsData.docStatus),
                                        ...docIndexChipSx,
                                    }}
                                />
                                {documentDetailsData.docStatus === 'OldIndex' && (
                                    <Chip
                                        label={t('newVersionAvailable')}
                                        sx={{
                                            ...docIndexChipSx,
                                            backgroundColor: vars.red,
                                        }}
                                    />
                                )}
                            </Stack>
                        ) : (
                            <Box sx={{ ...headingFontBold, fontSize: '12px', minHeight: '40px' }}>
                                {t('noDataPlaceholder')}
                            </Box>
                        )}
                    </Stack>
                </Box>
                <Box sx={{ ...setLeftPadding(isMobile) }}>
                    <FormFieldView
                        label={t('description')}
                        text={documentDetailsData.subject || defaultFormData?.Subject || ''}
                    />
                </Box>
            </Box>
            {!docTypeKeyForTemplateCreation && (
                <>
                    <Divider />
                    <Box
                        sx={{
                            ...gridBoxProps(isMobile),
                        }}
                    >
                        <Box sx={{ pt: 1 }}>
                            <FormFieldView
                                label={t('workflowName')}
                                text={
                                    documentDetailsData.wfTitle || documentDetailsData.wfType || ''
                                }
                                direction='row'
                                alignItems='center'
                            >
                                {(documentDetailsData.wfTitle || documentDetailsData.wfType) && (
                                    <DocStatusIcon
                                        type='Workflow'
                                        document={documentDetailsData}
                                        tooltipPlacement='top-start'
                                    />
                                )}
                            </FormFieldView>
                            <FormFieldView
                                label={t('creationDate')}
                                text={parseDate(documentDetailsData.creationDate)}
                                direction='row'
                                alignItems='center'
                            >
                                <AppIcon path={mdiPlusCircleOutline} tooltipPlacement='top-start' />
                            </FormFieldView>
                        </Box>
                        <Box sx={{ ...setLeftPadding(isMobile), pt: 1 }}>
                            <FormFieldView
                                label={t('workflowTask')}
                                text={documentDetailsData.wfTask || ''}
                                direction='row'
                                alignItems='center'
                            >
                                <DocStatusIcon
                                    type={documentDetailsData.docStatus}
                                    document={documentDetailsData}
                                    tooltipPlacement='top-start'
                                />
                            </FormFieldView>
                            <FormFieldView
                                label={t('creator')}
                                text={parseUsername(documentDetailsData.creator)}
                                direction='row'
                                alignItems='center'
                            >
                                <UserAvatar
                                    size={25}
                                    userName={parseUsername(documentDetailsData.creator)}
                                />
                            </FormFieldView>
                        </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                </>
            )}
            <FormDesigner
                submitRef={submitRef}
                docType={currentDocType}
                documentDetails={documentDetailsData}
                defaultFormData={defaultFormData}
            />
            <LeaveConfirmDialog
                open={isLeaveConfirmDialogOpen}
                handleClose={handleCancelLeaveConfirmDialog}
                handleLeave={handleCloseLeaveConfirmDialog}
                handleSaveChanges={handleSaveChangesLeaveConfirmDialog}
                title='closeWithoutSavingChanges'
                text='changesWillNotBeSaved'
            />
            {loadingRunDynamicAction && (
                <Stack
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        backgroundColor: `${vars.black}`,
                        opacity: 0.5,
                        zIndex: 1,
                        display: 'flex',
                        ...fullSize,
                    }}
                >
                    <CircularProgress />
                </Stack>
            )}
        </Stack>
    );
};

/**
 * Interface that describes the props for a DocumentDetailsView component.
 */
export interface IDocumentDetailsViewProps {
    /** Project ReplicaId. */
    replicaId: string;

    /** (OPTION) Defines if the content Component is Dialog or Browser tab. */
    isDialog?: boolean;
}
